import { Component, OnDestroy, OnInit } from '@angular/core';
import { OverlayContainer } from '@angular/cdk/overlay';
import { TranslateService } from '@ngx-translate/core';
import { I18nService } from '@app/core';
import { AdminConfigService } from '@app/core/admin-config.service';
import { Table } from '@models/table';
import { UserService } from '@app/core/user.service';
import { AuthService } from '@app/core/auth';
import { SensorService } from '@app/core/sensor.service';
import { Title } from '@angular/platform-browser';
import { StationService } from '@app/core/station.service';
import { ModuloService } from '@app/core/modulo.service';
import { tap } from 'rxjs/operators';
import { HttpErrorResponse } from '@angular/common/http';

interface MenuItem {
  title?: string;
  titleKey?: string;
  id?: string;
  icon: string;
  routerLink?: string;
  expanded?: boolean;
  children?: MenuItem[];
}

@Component({
  standalone: false,
  selector: 'app-shell',
  templateUrl: './shell.component.html',
  styleUrls: ['./shell.component.scss']
})
export class ShellComponent implements OnInit, OnDestroy {
  public collapsed = true;
  public tables: Table[] = [];
  public user: any = null;
  public currentLang = 'es';
  public isDarkTheme = false;
  private readonly themeStorageKey = 'theme';
  private sensorTypesRaw: any[] = [];
  private readonly overlayOpenHandler = () => this.closeSidebar();

  public menu: MenuItem[] = [
    {
      id: 'home',
      titleKey: 'MENU.HOME',
      icon: 'home',
      routerLink: '/shell/home'
    },
    {
      id: 'statics',
      titleKey: 'MENU.STATICS',
      icon: 'insert_chart',
      routerLink: '/shell/statics'
    },
    {
      id: 'calculos',
      titleKey: 'MENU.CALCULOS',
      icon: 'calculate',
      routerLink: '/shell/calculos'
    },
    {
      id: 'balance',
      titleKey: 'MENU.BALANCE',
      icon: 'water_drop',
      routerLink: '/shell/balance'
    },
    {
      id: 'config',
      titleKey: 'MENU.CONFIGURATION',
      icon: 'settings',
      expanded: false,
      children: []
    }
  ];

  constructor(
    private authService: AuthService,
    private userService: UserService,
    private translate: TranslateService,
    private i18nService: I18nService,
    private adminConfigService: AdminConfigService,
    private sensorService: SensorService,
    private stationService: StationService,
    private moduleService: ModuloService,
    private titleService: Title,
    private overlayContainer: OverlayContainer
  ) {}

  toggleSidebar() {
    this.collapsed = !this.collapsed;
    if (typeof window !== 'undefined') {
      setTimeout(() => window.dispatchEvent(new Event('resize')), 300);
    }
  }

  onMenuNavigate() {
    this.closeSidebar();
  }

  toggleGroup(item: MenuItem) {
    if (item.children && item.children.length) {
      item.expanded = !item.expanded;
    }
  }

  private addPestMenu(): void {
    const exists = this.menu.some(item => item.id === 'pest-models');
    if (exists) return;
    this.menu.splice(4, 0, {
      id: 'pest-models',
      titleKey: 'MENU.PEST_MODELS',
      icon: 'pest_control',
      routerLink: '/shell/pest-models'
    });
  }

  private removePestMenu(): void {
    this.menu = this.menu.filter(item => item.id !== 'pest-models');
  }

  private handleAuthError(err: any) {
    if (err instanceof HttpErrorResponse && err.status === 401) {
      return;
    }
  }

  private removeConfigMenu() {
    this.menu = this.menu.filter(item => item.id !== 'config');
  }

  private filterConfigChildren(deniedTables: string[]) {
    const config = this.configSection;
    if (!config) return;
    const deniedLinks = deniedTables.map(name => `/shell/table/${name}`);
    config.children = (config.children || []).filter(
      item => !deniedLinks.includes(item.routerLink || '')
    );
  }

  private applyMenuForRole(roleId: number | null) {
    if (roleId === null) {
      this.removeConfigMenu();
      this.removePestMenu();
      return;
    }

    if (roleId === 3) {
      this.setMenuBasico().then(() => {
        this.filterConfigChildren(['project_user', 'project_cliente', 'project_module', 'project_sensor', 'project_moisture_profile', 'project_station']);
        this.removePestMenu();
      });
      return;
    }

    this.setMenu().then(() => {
      if (roleId === 1) {
        this.addPestMenu();
      } else {
        this.removePestMenu();
      }
      if (roleId === 2) {
        this.filterConfigChildren(['project_user', 'project_cliente']);
      }
    });
  }

  ngOnInit() {
    const storedTheme = localStorage.getItem(this.themeStorageKey);
    this.applyTheme(storedTheme === 'dark');
    this.currentLang = (this.translate.currentLang || 'es').split('-')[0];
    this.translate.onLangChange.subscribe(event => {
      this.currentLang = event.lang.split('-')[0];
      this.applySensorTypesLocalization();
    });
    if (this.titleService.getTitle() !== 'TBAGROSENSOR') {
      this.titleService.setTitle('TBAGROSENSOR');
    }

    this.userService
      .getUserInfo()
      .pipe(
        tap(user => {
          this.user = user;
          const roleId = this.userService.getRoleId();
          this.applyMenuForRole(roleId);
        }),
        tap({ error: err => this.handleAuthError(err) })
      )
      .subscribe({ error: err => this.handleAuthError(err) });

    this.collapsed = true;

    if (typeof window !== 'undefined') {
      window.addEventListener('app-overlay-open', this.overlayOpenHandler);
    }

    this.sensorService
      .getSensorTypes()
      .pipe(
        tap(data => {
          this.sensorTypesRaw = data || [];
          this.applySensorTypesLocalization();
        }),
        tap({ error: err => this.handleAuthError(err) })
      )
      .subscribe({ error: err => this.handleAuthError(err) });

    this.stationService
      .getStationTypes()
      .pipe(
        tap(data => (this.stationService.stationTypes = data)),
        tap({ error: err => this.handleAuthError(err) })
      )
      .subscribe({ error: err => this.handleAuthError(err) });

    this.moduleService
      .getModulePowerSupplyTypes()
      .pipe(
        tap(data => (this.moduleService.modulePowerSupplyTypes = data)),
        tap({ error: err => this.handleAuthError(err) })
      )
      .subscribe({ error: err => this.handleAuthError(err) });
  }

  ngOnDestroy(): void {
    if (typeof window !== 'undefined') {
      window.removeEventListener('app-overlay-open', this.overlayOpenHandler);
    }
  }

  public setLanguage(language: string) {
    this.i18nService.set(language);
    this.currentLang = language;
  }

  public toggleTheme(force?: boolean) {
    const next = force !== undefined ? force : !this.isDarkTheme;
    this.applyTheme(next);
  }

  private applyTheme(isDark: boolean) {
    this.isDarkTheme = isDark;
    const className = 'dark-theme';
    document.body.classList.toggle(className, isDark);
    const overlayClasses = this.overlayContainer.getContainerElement()
      .classList;
    overlayClasses.toggle(className, isDark);
    localStorage.setItem(this.themeStorageKey, isDark ? 'dark' : 'light');
    if (typeof window !== 'undefined') {
      window.dispatchEvent(
        new CustomEvent('app-theme-change', {
          detail: { theme: isDark ? 'dark' : 'light' }
        })
      );
    }
  }

  logout() {
    this.authService.logout();
  }

  private closeSidebar(): void {
    this.collapsed = true;
    if (typeof window !== 'undefined') {
      setTimeout(() => window.dispatchEvent(new Event('resize')), 200);
    }
  }

  private buildConfigItem(element: Table): MenuItem {
    const routerLink = '/shell/table/' + element.getName();
    const titleKey = element.getLabel();

    const item: MenuItem = {
      titleKey,
      icon: 'table_chart',
      routerLink
    };

    if (element.getName() === 'project_finca') item.icon = 'home';
    if (element.getName() === 'project_module') item.icon = 'router';
    if (element.getName() === 'project_sensor') item.icon = 'device_thermostat';
    if (element.getName() === 'project_station') item.icon = 'sensors';
    if (element.getName() === 'project_cliente') item.icon = 'person';
    if (element.getName() === 'project_user') item.icon = 'group';

    return item;
  }

  public async setMenu() {
    await this.adminConfigService.onInit();
    this.tables = this.adminConfigService.getTables();

    const config = this.configSection;
    if (!config) return;
    config.children = [];

    for (const element of this.tables) {
      config.children.push(this.buildConfigItem(element));
    }
  }

  public async setMenuBasico() {
    await this.adminConfigService.onInit();
    this.tables = this.adminConfigService.getTables();

    const config = this.configSection;
    if (!config) return;
    config.children = [];

    for (const element of this.tables) {
      config.children.push(this.buildConfigItem(element));
    }

    // B?sico: quitamos estad?sticas del men? principal
  }

  private get configSection(): MenuItem | undefined {
    return this.menu.find(item => item.id === 'config');
  }

  private applySensorTypesLocalization(): void {
    if (!this.sensorTypesRaw) {
      return;
    }
    const localized = this.localizeSensorTypes(this.sensorTypesRaw);
    this.sensorService.sensorTypes.next(localized);
    this.sensorService.sensorTypesFixed = localized;
  }

  private localizeSensorTypes(types: any[] | null | undefined): any[] {
    if (!types) {
      return [];
    }
    return types.map(t => {
      const key = this.getSensorTypeNameKey(t?.value);
      const name = key ? this.translate.instant(key) : t?.name;
      return { ...t, name: name || t?.name || t?.value };
    });
  }

  private getSensorTypeNameKey(value: string | undefined): string | null {
    switch (value) {
      case 'TE':
        return 'STATICS.SENSOR_TYPES.AMBIENT_TEMP';
      case 'FL':
        return 'STATICS.SENSOR_TYPES.FLOW';
      case 'HU':
        return 'STATICS.SENSOR_TYPES.HUMIDITY';
      case 'SH':
        return 'STATICS.SENSOR_TYPES.SOIL_HUMIDITY';
      case 'SC':
        return 'STATICS.SENSOR_TYPES.SOIL_CONDUCTIVITY';
      case 'HH':
        return 'STATICS.SENSOR_TYPES.LEAF_HUMIDITY';
      case 'E8':
        return 'STATICS.SENSOR_TYPES.ENVIROPRO_8';
      case 'DE':
        return 'STATICS.SENSOR_TYPES.DENDROMETER';
      case 'ED':
        return 'SensorType_ED';
      case 'DIGITAL':
        return 'SensorType_DIGITAL';
      case 'PL':
        return 'STATICS.SENSOR_TYPES.PLUVIOMETER';
      case 'BA':
        return 'STATICS.SENSOR_TYPES.BATTERY';
      case 'AN':
        return 'STATICS.SENSOR_TYPES.ANEMOMETER';
      case 'CO':
      case 'WC':
        return 'STATICS.SENSOR_TYPES.CONDUCTIVITY';
      default:
        return null;
    }
  }
}
